import { useEffect, useState } from "react";
import { API } from "../../App";
import AdminLayout from "../../components/AdminLayout";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Plus, Pencil, Trash2, FolderOpen, ChevronRight, Loader2, Search, X } from "lucide-react";
import { toast } from "sonner";

export default function AdminCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    parent_id: "",
    image_url: "",
    is_active: true,
    sort_order: 0,
  });

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API}/categories/all`, { credentials: "include" });
      if (response.ok) setCategories(await response.json());
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    const payload = {
      ...formData,
      parent_id: formData.parent_id === "none" ? null : formData.parent_id || null,
      sort_order: parseInt(formData.sort_order) || 0,
    };

    try {
      const url = editingCategory ? `${API}/categories/${editingCategory.category_id}` : `${API}/categories`;
      const method = editingCategory ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error("Failed to save category");

      toast.success(editingCategory ? "Category updated!" : "Category created!");
      setIsDialogOpen(false);
      resetForm();
      fetchCategories();
    } catch (error) {
      toast.error("Failed to save category");
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (categoryId) => {
    if (!confirm("Are you sure you want to delete this category?")) return;

    try {
      const response = await fetch(`${API}/categories/${categoryId}`, { method: "DELETE", credentials: "include" });
      if (!response.ok) throw new Error("Failed to delete");
      toast.success("Category deleted!");
      fetchCategories();
    } catch (error) {
      toast.error("Failed to delete category");
    }
  };

  const openEditDialog = (category) => {
    setEditingCategory(category);
    setFormData({
      name: category.name,
      description: category.description || "",
      parent_id: category.parent_id || "",
      image_url: category.image_url || "",
      is_active: category.is_active,
      sort_order: category.sort_order || 0,
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingCategory(null);
    setFormData({ name: "", description: "", parent_id: "", image_url: "", is_active: true, sort_order: 0 });
  };

  const parentCategories = categories.filter((c) => !c.parent_id);
  const getSubcategories = (parentId) => categories.filter((c) => c.parent_id === parentId);

  return (
    <AdminLayout>
      <div className="animate-fade-in" data-testid="admin-categories">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Category Management</h1>
            <p className="text-gray-500 text-sm mt-1">Organize your menu with categories and subcategories</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-green-500 hover:bg-green-600" data-testid="add-category-btn">
                <Plus size={18} className="mr-2" /> Add Category
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md bg-white">
              <DialogHeader>
                <DialogTitle>{editingCategory ? "Edit Category" : "Add New Category"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Category Name</Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., Burgers, Pizzas" required className="bg-white" />
                </div>
                <div>
                  <Label>Description (Optional)</Label>
                  <Textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} placeholder="Brief description of this category" className="bg-white resize-y" />
                </div>
                <div>
                  <Label>Parent Category (Optional)</Label>
                  <Select value={formData.parent_id || "none"} onValueChange={(value) => setFormData({ ...formData, parent_id: value === "none" ? "" : value })}>
                    <SelectTrigger className="bg-white"><SelectValue placeholder="Select parent category" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Parent (Top Level)</SelectItem>
                      {parentCategories.filter(c => c.category_id !== editingCategory?.category_id).map((cat) => (
                        <SelectItem key={cat.category_id} value={cat.category_id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-gray-400 mt-1">Leave empty to create a main category</p>
                </div>
                <div>
                  <Label>Image URL (Optional)</Label>
                  <Input value={formData.image_url} onChange={(e) => setFormData({ ...formData, image_url: e.target.value })} placeholder="https://..." className="bg-white" />
                </div>
                <div>
                  <Label>Sort Order</Label>
                  <Input type="number" value={formData.sort_order} onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })} placeholder="0" className="bg-white" />
                  <p className="text-[10px] text-gray-400 mt-1">Lower numbers appear first</p>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${formData.is_active ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    <Label className="font-medium">Active</Label>
                  </div>
                  <Switch checked={formData.is_active} onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })} />
                </div>
                <Button type="submit" className="w-full bg-green-500 hover:bg-green-600" disabled={formLoading}>
                  {formLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : editingCategory ? "Update Category" : "Create Category"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Filter Bar */}
        <div className="mb-6 flex items-center gap-3" data-testid="categories-filter-bar">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder="Search categories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-11 bg-white"
              data-testid="categories-filter-name"
            />
          </div>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="w-40" data-testid="categories-filter-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="parent">Parent Only</SelectItem>
              <SelectItem value="sub">Subcategories</SelectItem>
            </SelectContent>
          </Select>
          {(searchQuery || typeFilter !== "all") && (
            <Button variant="ghost" size="sm" onClick={() => { setSearchQuery(""); setTypeFilter("all"); }} className="text-gray-500" data-testid="categories-filter-clear">
              <X size={14} className="mr-1" /> Clear
            </Button>
          )}
        </div>

        {(() => {
          // Filter categories
          const matchesSearch = (cat) => !searchQuery || cat.name.toLowerCase().includes(searchQuery.toLowerCase()) || (cat.description || "").toLowerCase().includes(searchQuery.toLowerCase());
          
          const filteredParents = parentCategories.filter((cat) => {
            if (typeFilter === "sub") return false;
            const subs = getSubcategories(cat.category_id);
            const parentMatches = matchesSearch(cat);
            const anySubMatches = subs.some(matchesSearch);
            return parentMatches || anySubMatches;
          });

          return loading ? (
          <div className="space-y-4">{[1, 2, 3].map((i) => <div key={i} className="skeleton h-20 w-full rounded-xl"></div>)}</div>
        ) : filteredParents.length === 0 && typeFilter !== "sub" ? (
          <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
            <FolderOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No categories found</p>
          </div>
        ) : typeFilter === "sub" ? (
          <div className="space-y-2 bg-white rounded-2xl border border-gray-100 p-4">
            <p className="text-[10px] text-gray-400 font-medium mb-3">All Subcategories</p>
            {categories.filter(c => c.parent_id && matchesSearch(c)).map((sub) => {
              const parent = parentCategories.find(p => p.category_id === sub.parent_id);
              return (
                <div key={sub.category_id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center gap-2">
                    <ChevronRight size={14} className="text-gray-400" />
                    <span className="text-gray-700">{sub.name}</span>
                    {parent && <span className="text-[10px] text-gray-500">in {parent.name}</span>}
                    <span className={`px-2 py-0.5 rounded-full text-xs ${sub.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                      {sub.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="ghost" size="sm" onClick={() => openEditDialog(sub)}><Pencil size={12} /></Button>
                    <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(sub.category_id)}><Trash2 size={12} /></Button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="space-y-4">
            {filteredParents.map((category) => (
              <div key={category.category_id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {category.image_url ? (
                      <img src={category.image_url} alt={category.name} className="w-12 h-12 rounded-lg object-cover" />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center"><FolderOpen className="w-6 h-6 text-gray-400" /></div>
                    )}
                    <div>
                      <h3 className="font-semibold text-gray-900">{category.name}</h3>
                      {category.description && <p className="text-gray-500 text-sm">{category.description}</p>}
                      <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${category.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                        {category.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEditDialog(category)}><Pencil size={14} /></Button>
                    <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(category.category_id)}><Trash2 size={14} /></Button>
                  </div>
                </div>
                {getSubcategories(category.category_id).length > 0 && (
                  <div className="border-t border-gray-100 bg-gray-50 p-4">
                    <p className="text-[10px] text-gray-400 font-medium mb-3">Subcategories</p>
                    <div className="space-y-2">
                      {getSubcategories(category.category_id).map((sub) => (
                        <div key={sub.category_id} className="flex items-center justify-between bg-white p-3 rounded-lg">
                          <div className="flex items-center gap-2">
                            <ChevronRight size={14} className="text-gray-400" />
                            <span className="text-gray-700">{sub.name}</span>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${sub.is_active ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>
                              {sub.is_active ? "Active" : "Inactive"}
                            </span>
                          </div>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(sub)}><Pencil size={12} /></Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(sub.category_id)}><Trash2 size={12} /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        );
        })()}
      </div>
    </AdminLayout>
  );
}
